import type { ReactNode } from "react"

export type Scenario = {
  label: string
  userMessage: string
  brackyMessage: string
  description: string
  descriptionMobile?: string | ReactNode
  userTimestamp: string
  brackyTimestamp: string
  username?: string
}

export type BrackyFeature = {
  title: string
  username: string
  scenarios: Scenario[]
}

export const brackyFeatures: BrackyFeature[] = [
  {
    title: "Bet Live In Your Feed",
    username: "CowboysFan99",
    scenarios: [
      {
        label: "Bet",
        userMessage: "@bracky put 500 on the Dallas Cowboys to win the Super Bowl 🤝",
        userTimestamp: "1m ago",
        brackyMessage: "Bet executed. 500 $BRACKY on Cobowys at 2% odds.",
        brackyTimestamp: "Now",
        description:
          "Just @bracky to bet on sports' biggest moments—right in your social feed. Winning bets always stack $BRACKY",
        descriptionMobile: "Message @bracky to bet sports' biggest moments in your feed. Winning bets stack $BRACKY",
      },
      {
        label: "Sell",
        userMessage: "@bracky ring-3 the register on my TCU Playoff position, Horned Frogs ngmi this year…",
        userTimestamp: "2m ago",
        brackyMessage: "Sell executed. Sold your full TCU position returning +2,800 $BRACKY to your Bracket balance.",
        brackyTimestamp: "1m ago",
        description:
          "Mention @bracky to sell your positions at any time. Lock in profits or cut losses before momentum turns.",
        descriptionMobile:
          "@bracky to sell your positions at any time. Lock in profits or cut losses before momentum turns.",
      },
      {
        label: "Adjust",
        userMessage: "@bracky rebalance my Super Bowl portfolio to 90% Cowboys, 5% Dolphins, 5% Giants.",
        userTimestamp: "3m ago",
        brackyMessage:
          "Adjustments made. Sold your Eagles position to match target balance of 90% Cowboys, with 5% in Dolphins and Giants.",
        brackyTimestamp: "2m ago",
        description:
          "@bracky handles complex adjustments with ease. Describe the moves you want to make and let Bracky handle the rest…",
        descriptionMobile: "@bracky to rebalance your positions. Outline targets and let Bracky handle the rest…",
      },
      {
        label: "Bundle",
        userMessage:
          "@bracky bet 1,000 on Cowboys to win Super Bowl, sell half my Stars position, and 5,000 on the Yankees to win World Series.",
        userTimestamp: "4m ago",
        brackyMessage:
          "Multi-leg bet executed! Cowboys Super Bowl (+300 shares), Stars Cup (-150 shares), Rangers World Series (+5,000 shares). Updated exposure: 20,750 $BRACKY.",
        brackyTimestamp: "3m ago",
        description:
          "@bracky executes multi-leg bets from one command. Make coordinated moves that work together to maximize your upside.",
        descriptionMobile: "Easily place multiple bets with @bracky in one shot, in any language.",
      },
    ],
  },
  {
    title: "Social Markets",
    username: "BruinsCup88",
    scenarios: [
      {
        label: "Follow",
        userMessage: "@bracky, what's @RollTide4life holding right now in the 26 CFB Bracket?",
        userTimestamp: "3m ago",
        brackyMessage:
          "@RollTide4life's active positions: Alabama (200K $BRACKY), Arkansas (150K), Tennesse (100K). Total position value: 450K $BRACKY.",
        brackyTimestamp: "2m ago",
        description: "Ask @bracky about anyone's open positions and get a full breakdown on their betting strategy.",
        descriptionMobile:
          "Ask @bracky about anyone's open positions and get a full breakdown on their betting strategy.",
      },
      {
        label: "Copy Position",
        userMessage: "@bracky copy @stoolpresidente's top position with 10% of my balance",
        userTimestamp: "4, ago",
        brackyMessage: "Position copied. 750 $BRACKY bet on Michigan, mirroring @stoolpresidente's current portfolio.",
        brackyTimestamp: "3m ago",
        description:
          "@bracky copies bets in one command. Instantly mirror winning strategies and keep in step with smart money.",
        descriptionMobile: (
          <>
            Mirror bets & copy others' moves in one tap.
            <br />
            Keep in step with smart money.
          </>
        ),
      },
      {
        label: "Invite",
        userMessage:
          "@bracky invite @BruinsFan617 to join me on Bracket with some Bruins shares, they're looking strong for the Cup!",
        userTimestamp: "6m ago",
        brackyMessage:
          "Hi @BruinsFan617! @HockeyGrind8 has invited you to join him betting the NHL Playoffs. I've set you up with 50 Bruins shares and 25K $BRACKY to start betting with. Welcome.",
        brackyTimestamp: "5m ago",
        description:
          "Tell @bracky to send betting tokens to your friends—they can use them right away to jump into brackets.",
        descriptionMobile: "Ask @bracky to send $BRACKY to get your friends into brackets in a flash.",
      },
    ],
  },
  {
    title: "AI Sports Intelligence™",
    username: "LakersLegacy23",
    scenarios: [
      {
        label: "Analyze",
        userMessage: "@bracky analyze my NBA Playoffs position and give me your ideas for better risk-reward.",
        userTimestamp: "2m ago",
        brackyMessage:
          "Your Lakers conviction (85% weight) is noted. Consider adding undervalued Pacers shares to hedge while keeping your Lakers loyalty intact. Reduces correlation risk from 73% to 45%.",
        brackyTimestamp: "1m ago",
        description:
          "@bracky analyzes your holdings and betting patterns. Ask for personalized insights to keep improving your game.",
        descriptionMobile: "@bracky analyzes your holdings and betting patterns and delivers personalized insights.",
      },
      {
        label: "Edge",
        userMessage: "@bracky, any undervalued World Series opportunities right now?",
        userTimestamp: "3m ago",
        brackyMessage:
          "Value alert: Padres World Series shares trade at 4% odds, but they're 8-2 in last 10 with Tatis heating up. Playoff momentum isn't priced in yet.",
        brackyTimestamp: "2m ago",
        description:
          "@bracky finds you undervalued plays by analyzing factors most miss: weather, trends, odds inefficiencies, and more.",
        descriptionMobile: "@bracky finds you undervalued plays by analyzing factors most miss.",
      },
      {
        label: "Predict",
        userMessage: "@bracky, predict who sneaks into the CFB Playoff?",
        userTimestamp: "4m ago",
        brackyMessage:
          "SMU has 67% chance to sneak in as 12-seed if they beat Clemson. Key factors: weak at-large pool, committee loves conference champs, and their strength of schedule improved.",
        brackyTimestamp: "3m ago",
        description:
          "@bracky forecasts outcomes using complex pattern analysis. Get predictive insights before the market catches up.",
        descriptionMobile: "@bracky forecasts outcomes by analyzing complex historical data patterns.",
      },
      {
        label: "Update",
        userMessage: "@bracky how are my LA Chargers looking tonight?",
        userTimestamp: "5m ago",
        brackyMessage:
          "Chargers are dominating the Raiders, up 28-7 in Q4 at SoFi Stadium. Herbert 3 TDs, defense forcing 3 turnovers. Your position value has increased by 15.5%, looking strong into final minutes.",
        brackyTimestamp: "4m ago",
        description:
          "@bracky monitors matchups and events in real-time so you always know how your positions are performing.",
        descriptionMobile: "@bracky monitors events in real-time so you always know how your positions are performing.",
      },
    ],
  },
]
